import React from 'react';
import { connect } from 'react-redux';
import { call, takeEvery } from 'redux-saga/effects';
import moment from 'moment';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import {
  SystemBackgroundTasksTable,
  refetchTable,
  rotateEncryptionKey,
  rotateIntegratorVaultKey,
} from '@kineticdata/react';
import { generateEmptyBodyRow, Icon } from '@kineticdata/ui';
import { action, dispatch, regHandlers, regSaga } from '../../store';
import { StatusBadge } from './StatusBadge';
import { ControlGroup } from './ControlGroup';
import { deleteSystemBackgroundTask } from '@kineticdata/react/src/apis/system';

regSaga(
  takeEvery('REQUEST_ENCRYPTION_KEY_ROTATION', function* ({ payload }) {
    try {
      const { tableKey, spaceSlug } = payload;
      yield call(rotateEncryptionKey, { spaceSlug });
      yield call(refetchTable, tableKey);
    } catch (e) {
      console.log(
        'There was an error while requesting a System Encryption Key Rotation.',
      );
    }
  }),
);

regSaga(
  takeEvery('REQUEST_INTEGRATOR_KEY_ROTATION', function* ({ payload }) {
    try {
      const { tableKey, spaceSlug } = payload;
      yield call(rotateIntegratorVaultKey, { spaceSlug });
      yield call(refetchTable, tableKey);
    } catch (e) {
      console.log(
        'There was an error while requesting a System Integrator Key Rotation.',
      );
    }
  }),
);

regHandlers({
  OPEN_BACKGROUND_TASK_MODAL: (state, { payload }) =>
    state.setIn(['page', 'system', 'setup', 'security', 'viewTask'], payload),
  CLOSE_BACKGROUND_TASK_MODAL: state =>
    state.deleteIn(['page', 'system', 'setup', 'security', 'viewTask']),
});

const requestEncryptionKeyRotation =
  ({ tableKey, spaceSlug }) =>
  () =>
    dispatch(
      action('REQUEST_ENCRYPTION_KEY_ROTATION', { spaceSlug, tableKey }),
    );
const requestIntegratorVaultKey =
  ({ tableKey, spaceSlug }) =>
  () =>
    dispatch(
      action('REQUEST_INTEGRATOR_KEY_ROTATION', { spaceSlug, tableKey }),
    );
const openModal = backgroundTask => () =>
  dispatch(action('OPEN_BACKGROUND_TASK_MODAL', backgroundTask));
const closeModal = () => dispatch(action('CLOSE_BACKGROUND_TASK_MODAL'));
const handleRefresh = tableKey => () => refetchTable(tableKey);

const EmptyBodyRow = generateEmptyBodyRow({
  loadingMessage: 'Loading Background Tasks...',
  noSearchResultsMessage:
    'No Background Tasks were found - please modify your search criteria',
  noItemsMessage: 'There are no Background Tasks to display.',
});

const StatusBadgeCell = ({ value }) => (
  <td>
    <StatusBadge value={value} />
  </td>
);

const LinkingCell = ({ value, row }) => (
  <td>
    <button type="button" className="btn btn-text" onClick={openModal(row)}>
      {value}
    </button>
  </td>
);

const ActionsCell = ({ row, tableKey }) => (
  <td>
    <ControlGroup
      controlKey={row.get('id')}
      actions={{
        delete: id => deleteSystemBackgroundTask({ id }),
      }}
      confirmations={{
        delete: {
          title: 'Are you sure you want to delete this row?',
          body: 'Deleting this is a permanent action. Are you sure you want to continue?',
          actionName: 'Delete Background Task',
          actionType: 'danger',
        },
      }}
      onSuccess={() => {
        refetchTable(tableKey);
      }}
    >
      {({ actions, anyPending, pending }) => (
        <div className="btn-set">
          <button
            className="btn btn-table"
            onClick={openModal(row)}
            aria-label="Edit"
            title="Edit"
          >
            <Icon name="edit-2" md />
          </button>
          <button
            className="btn btn-table"
            type="button"
            disabled={anyPending}
            onClick={actions.delete(row.get('id'))}
            aria-label="Delete"
            title="Delete"
          >
            {pending.get('delete') ? (
              <Icon name="circle-notch" md spin />
            ) : (
              <Icon name="trash" md />
            )}
          </button>
        </div>
      )}
    </ControlGroup>
  </td>
);

const CreatedCell = ({ value }) => (
  <td>{moment(value).format('YYYY-MM-DD HH:mm:ss')}</td>
);

const formatTimestamp = timestamp =>
  moment(timestamp).format('YYYY-MM-DD HH:mm:ss');
const BackgroundTaskModal = ({ backgroundTask }) =>
  backgroundTask ? (
    <Modal isOpen size="lg" toggle={closeModal}>
      <ModalHeader>Background Task: {backgroundTask.get('id')}</ModalHeader>
      <ModalBody>
        <dl>
          <dt>Description</dt>
          <dd>{backgroundTask.get('description')}</dd>
          {backgroundTask.get('result') && (
            <>
              <dt>Result Status</dt>
              <dd>{backgroundTask.getIn(['result', 'status'])}</dd>
              <dt>Result Content</dt>
              <dd>
                <div className="pre-wrap">
                  {backgroundTask.getIn(['result', 'content'])}
                </div>
              </dd>
            </>
          )}
          {backgroundTask.get('exception') && (
            <>
              <dt>Exception Message</dt>
              <dd>
                <div className="pre-wrap">
                  {backgroundTask.get('exception')}
                </div>
              </dd>
            </>
          )}

          <dt>Transitions</dt>
          <dd>
            <ul className="list-unstyled">
              {backgroundTask.get('transitions').map(transition => {
                const [status, timestamp] = transition.entrySeq().first();
                return (
                  <li key={`${status}-${timestamp}`}>
                    <StatusBadge value={status} sm />
                    &nbsp;at&nbsp;
                    {formatTimestamp(timestamp)}
                  </li>
                );
              })}
            </ul>
          </dd>
          <dt>Messages</dt>
          <dd>
            {backgroundTask.get('messages').map((message, i) => (
              <li key={`${i}-${message.get('timestamp')}`}>
                [{formatTimestamp(message.get('timestamp'))}] -{' '}
                {message.get('message')}
              </li>
            ))}
          </dd>
        </dl>
      </ModalBody>
      <ModalFooter>
        <button
          type="button"
          className="btn btn-link-primary"
          onClick={closeModal}
        >
          Close
        </button>
      </ModalFooter>
    </Modal>
  ) : null;

const mapStateToProps = state => ({
  viewTask: state.getIn(['page', 'system', 'setup', 'security', 'viewTask']),
  showTaskEncryptionRotation:
    state.getIn(['tenant', 'features', 'taskEnabled']) || false,
  showIntegratorVaultRotation:
    state.getIn(['tenant', 'features', 'integratorEnabled']) || false,
});

export const BackgroundTasksList = connect(mapStateToProps)(
  ({
    viewTask,
    tableKey,
    spaceSlug,
    omitTitle,
    showTaskEncryptionRotation = false,
    showIntegratorVaultRotation = false,
  }) => (
    <>
      <div className="heading heading-sm">
        {!omitTitle && <h2 className="h3">Background Tasks</h2>}
        <div className="btn-set ms-auto">
          <button
            type="button"
            className="btn btn-outline-primary btn-sm"
            onClick={handleRefresh(tableKey)}
            aria-label="Refresh"
            title="Refresh"
          >
            <Icon name="refresh-cw" />
          </button>
          {(showTaskEncryptionRotation || !spaceSlug) && (
            <button
              type="button"
              className="btn btn-outline-primary btn-sm"
              onClick={requestEncryptionKeyRotation({ tableKey, spaceSlug })}
            >
              {spaceSlug
                ? 'Rotate Task Encryption Key'
                : 'Rotate Agent Encryption Key'}
            </button>
          )}
          {showIntegratorVaultRotation && (
            <button
              type="button"
              className="btn btn-outline-primary btn-sm"
              onClick={requestIntegratorVaultKey({ tableKey, spaceSlug })}
            >
              Rotate Integrator Vault Key
            </button>
          )}
        </div>
      </div>

      <div className="content-container">
        <BackgroundTaskModal backgroundTask={viewTask} />

        <SystemBackgroundTasksTable
          tableKey={tableKey}
          spaceSlug={spaceSlug}
          components={{ EmptyBodyRow }}
          columnSet={['status', 'createdAt', 'description', '_actions']}
          alterColumns={{
            status: { components: { BodyCell: StatusBadgeCell } },
            description: {
              components: { BodyCell: LinkingCell },
            },
            createdAt: { components: { BodyCell: CreatedCell } },
          }}
          addColumns={[
            {
              value: '_actions',
              title: '',
              sortable: false,
              components: { BodyCell: ActionsCell },
            },
          ]}
          defaultSortColumn="createdAt"
        >
          {({ table }) => table}
        </SystemBackgroundTasksTable>
      </div>
    </>
  ),
);
